import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Form } from "@/components/ui/form";
import { useToast } from '@/hooks/use-toast';
import { EventFormFields } from './form/EventFormFields';
import { ImageSection } from './form/ImageSection';
import type { GalleryEvent, GalleryEventFormValues } from '@/types/gallery';

interface GalleryEventEditorProps {
  initialData?: GalleryEvent;
  onSave: () => void;
  onRefresh?: () => void | Promise<void>;
}

const GalleryEventEditor = ({ initialData, onSave, onRefresh }: GalleryEventEditorProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tempEventId, setTempEventId] = useState<string | null>(null);
  const eventId = initialData?.id || tempEventId;
  
  const form = useForm<GalleryEventFormValues>({
    defaultValues: {
      title_en: initialData?.title_en || '',
      title_ko: initialData?.title_ko || '',
      description_en: initialData?.description_en || '',
      description_ko: initialData?.description_ko || '',
      event_date: initialData?.event_date || '',
      location: initialData?.location || '',
      category: initialData?.category || '',
      is_featured: initialData?.is_featured || false,
    },
  });

  const onSubmit = async (data: GalleryEventFormValues) => {
    setIsSubmitting(true);
    try {
      const eventData = data;

      if (eventId) {
        const { error } = await supabase
          .from('gallery_events')
          .update({
            ...eventData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', eventId);

        if (error) throw error;
        await onRefresh?.();

        toast({
          title: 'Success',
          description: 'Event details updated successfully',
        });

        if (initialData) {
          onSave();
        }
      } else {
        const { data: newEvent, error } = await supabase
          .from('gallery_events')
          .insert([eventData])
          .select()
          .single();

        if (error) throw error;
        setTempEventId(newEvent.id);
        await onRefresh?.();

        toast({
          title: 'Event created',
          description: 'You can now upload images and choose a cover image.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <h3 className="text-lg font-semibold">Step 1: Event Details</h3>
          <p className="text-sm text-muted-foreground">
            Create the event first. Once saved, the image upload section will appear below.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <EventFormFields form={form} section="basic" />
          <div>
            <EventFormFields form={form} section="details" />
          </div>
        </div>

        {eventId ? (
          <ImageSection eventId={eventId} onImagesChanged={onRefresh} />
        ) : (
          <div className="rounded-lg border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
            Step 2: Images will be available after you create the event.
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onSave()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : eventId ? 'Save Event Details' : 'Create Event'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default GalleryEventEditor;